/**
 * A MEGŐRZÉS ÉS A MUNKAMENET KÖZTI VARRAT.
 *
 * A `session-storage.ts` a bájtokat tartja, a `useCaptureSession` a
 * képernyőn lévő sorozatot. Ez a hook dönti el, MIKOR mozog adat a kettő
 * között — és ez a hármas közül az egyetlen, ahol sorrendi hibát lehet
 * véteni.
 *
 * ── A HÁROM ÁLLAPOT, ÉS AMIÉRT KELL MIND A HÁROM ────────────────────────
 *
 *   `checking` — megnézzük, van-e megőrzött sorozat. ITT MÉG NEM ÍRUNK.
 *   `asking`   — van, és a felhasználó döntésére várunk.
 *   `live`     — a sorozat a képernyőn az igazság, és minden változása kiül
 *                a tárolóba.
 *
 * ⚠️ AZ ÍRÁS CSAK `live` ÁLLAPOTBAN INDUL EL, ÉS EZ NEM ÓVATOSSÁG. A kamera
 * ÜRES sorozattal nyílik; ha a mentés már a betöltés alatt futna, az első
 * kirajzolás KITÖRÖLNÉ a tegnapi lapokat, mielőtt bárki megkérdezné, kellenek
 * -e. A megőrzés pont azt a munkát semmisítené meg, amiért készült.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  clearSession,
  loadSession,
  type StoredPage,
  saveSession,
  sweepExpired,
} from './session-storage';

export interface PendingSession {
  pageCount: number;
  /** Mikor kezdte a sorozatot — a kérdés ezt mondja meg neki. */
  startedAt: number;
}

export interface Retention {
  /** `null`, amíg nincs mit felajánlani; addig a fotózás szabad. */
  pending: PendingSession | null;
  /** A megőrzött lapok átvétele. */
  resume: () => void;
  /** A megőrzött sorozat eldobása — a felhasználó újrakezdi. */
  discard: () => void;
  /** Végleges törlés: sikeres küldés és explicit „Mégsem" után. */
  forget: () => void;
}

/**
 * @param storageKey Melyik kéréshez tartozik a sorozat. `null` esetén nincs
 *   megőrzés — a hook ilyenkor is meghívható, csak nem csinál semmit.
 * @param pages A képernyőn lévő sorozat; ennek minden változása kiül.
 * @param adopt A megőrzött lapok átvétele a munkamenetbe.
 */
export function useRetention(
  storageKey: string | null,
  pages: readonly StoredPage[],
  adopt: (pages: StoredPage[]) => void
): Retention {
  const [status, setStatus] = useState<'checking' | 'asking' | 'live'>('checking');
  const [pending, setPending] = useState<PendingSession | null>(null);

  // A MEGŐRZÖTT LAPOK, amíg a felhasználó nem döntött. Nem állapotban: a
  // bájtokat nem kell kirajzolni, és egy állapot minden lapnál újrarajzolna.
  const offered = useRef<StoredPage[] | null>(null);
  const adoptRef = useRef(adopt);
  adoptRef.current = adopt;

  useEffect(() => {
    if (!storageKey) {
      setStatus('live');
      return;
    }

    let cancelled = false;
    void (async () => {
      // A TAKARÍTÁS A NYITÁSSAL EGYÜTT MEGY. Külön alkalom nincs rá: a
      // kamera megnyitása az egyetlen biztos pont, ahol az app fut és van
      // ideje. A `sweepExpired` a MÁSIK kérések lejárt sorozatait viszi —
      // azokat a `loadSession` soha nem látja.
      await sweepExpired();
      const found = await loadSession(storageKey);
      if (cancelled) {
        return;
      }

      if (found) {
        offered.current = found.pages;
        setPending({ pageCount: found.pages.length, startedAt: found.startedAt });
        setStatus('asking');
      } else {
        setStatus('live');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [storageKey]);

  // A KIÍRÁS. A `pages` tömb azonossága minden változásnál új, tehát ez
  // pontosan annyiszor fut, ahányszor a sorozat valóban módosult.
  useEffect(() => {
    if (!storageKey || status !== 'live') {
      return;
    }
    void saveSession(storageKey, pages);
  }, [storageKey, status, pages]);

  const resume = useCallback(() => {
    const taken = offered.current;
    offered.current = null;
    setPending(null);
    if (taken) {
      adoptRef.current(taken);
    }
    // A `live` az átvétel UTÁN — a React 18 mindkét állapotváltást egy
    // kirajzolásba fogja, tehát a sorrend itt nem dönt el semmit. Így írva
    // viszont OLVASVA is az a sorrend, ami történik: előbb megvannak a
    // lapok, utána kezd írni róluk.
    setStatus('live');
  }, []);

  const discard = useCallback(() => {
    offered.current = null;
    setPending(null);
    // ⚠️ NINCS KÜLÖN `clearSession`, ÉS EZ NEM FELEDÉKENYSÉG. A `live`-ra
    // váltás után a kiíró effekt az ÜRES sorozattal fut le, a `saveSession`
    // pedig az üres listára törli a fejlécet ÉS az összes árva lapot. Egy
    // második törlés ugyanazt csinálná, csak két helyen kellene karbantartani
    // — és egy őr, ami két úton is zöld, egyiket sem méri.
    setStatus('live');
  }, []);

  const forget = useCallback(() => {
    offered.current = null;
    setPending(null);
    if (storageKey) {
      void clearSession(storageKey);
    }
  }, [storageKey]);

  return { pending, resume, discard, forget };
}
